/**
 * Marketplace and entitlements contracts (WP4-MARKET, BUILD-PLAN M23,
 * research/library/marketplace.md). Imported as `@velora/contracts/marketplace`.
 *
 * - The module catalog (`MARKETPLACE_CATALOG`) is the single list of paid
 *   add-ons: key, price, trial, copy for the store and detail pages, usage
 *   allowance and the data-access manifest. Other modules gate on these keys
 *   (`entitlementKey` in their manifest) and the shell reads the copy for its
 *   locked panels.
 * - The API (`/v1/billing-saas/*`) serves the live per-office state on top of
 *   it. Square is the rail (ADR-0011); entitlements, trials and meters are
 *   Velora's, so nothing here names Square ids except invoice references.
 * - Money is integer cents. Dates are ISO strings; local billing dates are
 *   `YYYY-MM-DD` in the practice's billing time zone.
 */
import { z } from 'zod';

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const IsoInstant = z.string().min(10);
const Cents = z.number().int();

/* ------------------------------------------------------------------------ */
/* Keys                                                                      */
/* ------------------------------------------------------------------------ */

/**
 * Entitlement keys of every Marketplace module. A module's web and API
 * manifests use the same key as `entitlementKey` (for example the reviews
 * module gates on `reviews`, the AI receptionist on `ai_receptionist`).
 */
export const MARKETPLACE_MODULE_KEYS = [
  'texting',
  'reviews',
  'ai_receptionist',
  'call_intelligence',
  'ai_scribe',
  'analytics_pro',
  'imaging_ai',
  'imaging_3d',
  'imaging_scans',
  'lightboard',
  'radio',
  'online_booking',
  'membership',
  'ai_assistant',
  'team',
  'compliance',
  'tablet_checkin',
  'ppo_analysis',
  'websites',
  // Bundles (owner-research-marketplace.md section 3): sold like a module,
  // they grant every member module's key (`bundleOf`). Since owner feedback
  // 2026-10-28 they are the Marketplace's packages (`MARKETPLACE_PACKAGES`):
  // `engage` is the Patient service and communication package.
  'engage',
  'ai_suite',
  'production',
  'analytics_suite',
] as const;
export const MarketplaceModuleKeySchema = z.enum(MARKETPLACE_MODULE_KEYS);
export type MarketplaceModuleKey = z.infer<typeof MarketplaceModuleKeySchema>;

export function isMarketplaceModuleKey(value: string): value is MarketplaceModuleKey {
  return (MARKETPLACE_MODULE_KEYS as readonly string[]).includes(value);
}

/** Intent categories (BUILD-PLAN M23), in store order. */
export const MARKETPLACE_CATEGORIES = [
  { id: 'fill', label: 'Fill the schedule' },
  { id: 'talk', label: 'Talk to patients' },
  { id: 'calls', label: 'Answer every call' },
  { id: 'chart', label: 'Chart faster' },
  { id: 'paid', label: 'Get paid' },
  { id: 'numbers', label: 'Know your numbers' },
  { id: 'office', label: 'Run the office' },
] as const;
export const MarketplaceCategorySchema = z.enum([
  'fill',
  'talk',
  'calls',
  'chart',
  'paid',
  'numbers',
  'office',
]);
export type MarketplaceCategory = z.infer<typeof MarketplaceCategorySchema>;

/** Colour family of a module's icon tile (token ramps, never raw colours). */
export const ModuleToneSchema = z.enum([
  'blue',
  'amber',
  'violet',
  'green',
  'red',
  'orange',
  'navy',
  'charcoal',
]);
export type ModuleTone = z.infer<typeof ModuleToneSchema>;

export const ModuleBadgeSchema = z.enum(['popular', 'new', 'ai', 'writeback', 'desktop', 'beta']);
export type ModuleBadge = z.infer<typeof ModuleBadgeSchema>;

export const PmsSystemSchema = z.enum(['opendental', 'dentrix', 'eaglesoft']);
export type PmsSystem = z.infer<typeof PmsSystemSchema>;

export const PMS_LABEL: Readonly<Record<PmsSystem, string>> = {
  opendental: 'Open Dental',
  dentrix: 'Dentrix',
  eaglesoft: 'Eaglesoft',
};

/**
 * PMS write-back commands a module can issue. Mirrors the pms-gateway
 * `CommandType` names; availability per office comes from that office's
 * capability manifest (PulseSync v1 gating, BUILD-PLAN 2.5.3).
 */
export const WritebackCommandSchema = z.enum([
  'appointment.update',
  'appointment.cancel',
  'appointment.confirm',
  'appointment.create',
  'patient.update',
  'patient.create',
  'recall.sent',
  'note.add',
  'procedure.create',
  'procedure.update',
  'payment.post',
  'treatment_plan.update',
]);
export type WritebackCommand = z.infer<typeof WritebackCommandSchema>;

export const WRITEBACK_LABEL: Readonly<Record<WritebackCommand, string>> = {
  'appointment.update': 'Move or change appointments',
  'appointment.cancel': 'Break appointments',
  'appointment.confirm': 'Mark appointments confirmed',
  'appointment.create': 'Book appointments',
  'patient.update': 'Update patient contact details',
  'patient.create': 'Create new patients',
  'recall.sent': 'Record recall contact',
  'note.add': 'Add notes and communication log entries',
  'procedure.create': 'Chart procedures and conditions',
  'procedure.update': 'Update charted procedures',
  'payment.post': 'Post payments',
  'treatment_plan.update': 'Update treatment plans',
};

/* ------------------------------------------------------------------------ */
/* Catalog                                                                   */
/* ------------------------------------------------------------------------ */

export const DataAccessRowSchema = z.object({
  data: z.string(),
  access: z.enum(['read', 'write', 'read_write', 'processed', 'none']),
  note: z.string(),
  /** PMS commands this row writes back through (empty: Velora-only or read). */
  writeback: z.array(WritebackCommandSchema),
});
export type DataAccessRow = z.infer<typeof DataAccessRowSchema>;

export const UsageAllowanceSchema = z.object({
  /** `usage_meter.feature_key` the meter counts. */
  meterKey: z.string(),
  label: z.string(),
  /** Singular unit for copy ("text", "minute"). */
  unit: z.string(),
  includedPerOffice: z.number().int().positive(),
  /** Price per unit above the allowance, in hundredths of a cent (0.02 USD = 200). */
  overageMilliCents: z.number().int().nonnegative(),
});
export type UsageAllowance = z.infer<typeof UsageAllowanceSchema>;

/**
 * Core / Module / Usage classification of owner-research-marketplace.md
 * section 3: `module` is a monthly per-office price; `module_usage` adds a
 * metered allowance; `service` is a module with a one-time setup fee.
 */
export const PricingClassSchema = z.enum(['module', 'module_usage', 'service', 'bundle']);
export type PricingClass = z.infer<typeof PricingClassSchema>;

export const PricingSchema = z.object({
  class: PricingClassSchema,
  /** The research's suggested range per office per month; null when it gives none. */
  rangeCents: z.object({ min: Cents.nonnegative(), max: Cents.nonnegative() }).nullable(),
  /**
   * True while `priceCents` is a placeholder (the range midpoint, or the old
   * price where the research gives no range) for the owner to set in the
   * operator console.
   */
  placeholder: z.boolean(),
  /** One-time setup fee (services such as Websites). */
  setupFeeCents: Cents.nonnegative().nullable(),
  /** One-time alternative to the monthly price ("PPO report"). */
  oneTime: z.object({ label: z.string(), cents: Cents.nonnegative() }).nullable(),
});
export type Pricing = z.infer<typeof PricingSchema>;

export const CatalogModuleSchema = z.object({
  key: MarketplaceModuleKeySchema,
  name: z.string(),
  shortName: z.string(),
  by: z.string(),
  category: MarketplaceCategorySchema,
  tone: ModuleToneSchema,
  /** Lucide icon name (kebab-case). */
  icon: z.string(),
  /** List price per office per month. See `pricing.placeholder`. */
  priceCents: Cents.nonnegative(),
  pricing: PricingSchema,
  /** Member modules when this entry is a bundle; empty for a module. */
  bundleOf: z.array(MarketplaceModuleKeySchema),
  trialDays: z.number().int().min(0),
  badges: z.array(ModuleBadgeSchema),
  /** Ten words or so: the card's value line. */
  valueLine: z.string(),
  description: z.string(),
  /** Product facts (never invented outcome statistics). */
  facts: z.array(z.object({ value: z.string(), label: z.string() })),
  features: z.array(z.object({ title: z.string(), detail: z.string() })),
  steps: z.array(z.object({ title: z.string(), detail: z.string() })),
  /** "Live in about 5 minutes", prerequisites; never a licence key. */
  setup: z.string(),
  requires: z.array(z.string()),
  usage: UsageAllowanceSchema.nullable(),
  dataAccess: z.array(DataAccessRowSchema),
  phiLeavesVelora: z.boolean(),
  baaCovered: z.boolean(),
  /** Where the module lives once on, for "Open" links. */
  appPath: z.string().nullable(),
  /** One sentence for in-context locked panels. */
  lockedPitch: z.string(),
});
export type CatalogModule = z.infer<typeof CatalogModuleSchema>;

const W = (
  data: string,
  access: DataAccessRow['access'],
  note: string,
  writeback: WritebackCommand[] = [],
): DataAccessRow => ({ data, access, note, writeback });

/**
 * List price at the midpoint of the research's range (owner-research-marketplace.md
 * section 3), in whole dollars, marked as a placeholder for the owner to set.
 */
function midpoint(
  minDollars: number,
  maxDollars: number,
  cls: PricingClass = 'module',
  extra: Partial<Pick<Pricing, 'setupFeeCents' | 'oneTime'>> = {},
): { priceCents: number; pricing: Pricing } {
  const min = minDollars * 100;
  const max = maxDollars * 100;
  return {
    priceCents: Math.round((min + max) / 200) * 100,
    pricing: {
      class: cls,
      rangeCents: { min, max },
      placeholder: true,
      setupFeeCents: extra.setupFeeCents ?? null,
      oneTime: extra.oneTime ?? null,
    },
  };
}

/** The research gives no range: keep this price, still a placeholder. */
function unranged(
  dollars: number,
  cls: PricingClass = 'module',
): { priceCents: number; pricing: Pricing } {
  return {
    priceCents: dollars * 100,
    pricing: {
      class: cls,
      rangeCents: null,
      placeholder: true,
      setupFeeCents: null,
      oneTime: null,
    },
  };
}

/** The Marketplace catalog. Prices are per office per month. */
export const MARKETPLACE_CATALOG: readonly CatalogModule[] = [
  {
    key: 'texting',
    name: 'Warp 9 Texting',
    shortName: 'Warp 9 Texting',
    by: 'Velora',
    category: 'talk',
    tone: 'orange',
    icon: 'message-square',
    ...midpoint(99, 149, 'module_usage'),
    bundleOf: [],
    trialDays: 14,
    badges: ['popular', 'writeback'],
    valueLine:
      'Two-way texting from your office number, plus reminders, confirmations and missed-call text back.',
    description:
      'Warp 9 puts every patient conversation in one shared inbox the whole front desk can work. Reminders and confirmations go out on your schedule, replies such as "C" confirm the appointment, and a missed call gets a text from the same number so the patient books with you.',
    facts: [
      { value: '1 inbox', label: 'shared by every workstation at the office' },
      { value: 'STOP', label: 'opt-out and quiet hours handled for you' },
      { value: '2,500', label: 'texts included per office each month' },
    ],
    features: [
      {
        title: 'Shared inbox',
        detail: 'Assign, snooze and close threads, with unread counts per office.',
      },
      {
        title: 'Smart confirmations',
        detail: 'A "C" reply confirms the appointment and writes back where your PMS allows it.',
      },
      {
        title: 'Missed-call text back',
        detail: 'Sent from the same office number when Phones sees a missed call.',
      },
      {
        title: 'Templates with merge fields',
        detail: 'Preferred name, provider, time and office.',
      },
      { title: 'Quiet hours and opt-out', detail: 'Sending windows per office and STOP handling.' },
      { title: 'Recall lists', detail: 'Hygiene and unscheduled treatment lists ready to text.' },
      {
        title: 'Text your recall and benefits campaigns',
        detail: 'Campaigns are built in core Velora; this module sends them by text.',
      },
    ],
    steps: [
      {
        title: 'Text-enable your number',
        detail:
          'Keep your office number. Outbound texting turns on once carrier registration is approved.',
      },
      {
        title: 'Pick the reminder cadence',
        detail: 'Start from the 7-day, 2-day and same-day defaults.',
      },
      { title: 'Go live', detail: 'Messages appear in Communications for everyone at the office.' },
    ],
    setup: 'Live in about 10 minutes. Outbound texts wait for carrier registration.',
    requires: [],
    usage: {
      meterKey: 'sms',
      label: 'Texts',
      unit: 'text',
      includedPerOffice: 2500,
      overageMilliCents: 300,
    },
    dataAccess: [
      W('Appointments', 'read_write', 'Confirmation status only', ['appointment.confirm']),
      W('Patient contact info', 'read', 'Mobile number, preferred name and opt-out'),
      W('Communication log', 'write', 'Copies each text into the PMS log', ['note.add']),
      W('Recall due dates', 'read', 'For recall lists'),
    ],
    phiLeavesVelora: true,
    baaCovered: true,
    appPath: '/communications',
    lockedPitch: 'Text patients from your office number and let confirmations update the schedule.',
  },
  {
    key: 'reviews',
    name: 'Reviews',
    shortName: 'Reviews',
    by: 'Velora',
    category: 'talk',
    tone: 'orange',
    icon: 'star',
    ...unranged(79),
    bundleOf: [],
    trialDays: 14,
    badges: [],
    valueLine: 'Asks every patient for a Google review after checkout, and answers feedback fast.',
    description:
      'Reviews sends one short request after a completed visit, to every patient, with a direct link to your Google profile. Nobody is filtered by how they might rate you. Replies to new public reviews are drafted for approval, and any patient who writes to the office privately lands in a thread the office manager can answer.',
    facts: [
      { value: '1 request', label: 'per patient per 90 days at most' },
      { value: 'Every patient', label: 'asked the same way (no review gating)' },
      { value: 'Per office', label: 'each office keeps its own Google profile' },
    ],
    features: [
      { title: 'Post-visit requests', detail: 'Sent on checkout, once per patient per 90 days.' },
      { title: 'No gating', detail: 'The same link goes to everyone, as Google requires.' },
      { title: 'Reply drafts', detail: 'Drafted replies you approve before they are posted.' },
      { title: 'Per-office profiles', detail: 'Each office links its own Google profile.' },
    ],
    steps: [
      { title: 'Connect Google profiles', detail: 'One per office. Takes about a minute.' },
      {
        title: 'Choose the trigger',
        detail: 'At checkout, or a set number of hours after the visit.',
      },
      { title: 'Watch the score', detail: 'Track trends in Reviews and on Home.' },
    ],
    setup: 'Live in about 5 minutes per office.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Completed appointments', 'read', 'Trigger only'),
      W('Patient contact info', 'read', 'Mobile number and email'),
      W('Communication log', 'write', 'Logs that a request was sent', ['note.add']),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/reviews',
    lockedPitch:
      'Ask every patient for a review after checkout and keep each office’s rating climbing.',
  },
  {
    key: 'ai_receptionist',
    name: 'Digital Receptionist',
    shortName: 'Digital Receptionist',
    by: 'Velora',
    category: 'calls',
    tone: 'violet',
    icon: 'phone-call',
    ...midpoint(199, 299, 'module_usage'),
    bundleOf: [],
    trialDays: 14,
    badges: ['new', 'ai', 'writeback'],
    valueLine: 'Answers overflow and after-hours calls, answers questions and books open time.',
    description:
      'Digital Receptionist picks up when the front desk cannot: on the fourth ring, during lunch and after hours. It knows your hours, accepted insurance, providers and policies, books into openings that follow your scheduling rules, and ends every call with a summary and transcript in Communications. Emergencies go straight to the on-call number.',
    facts: [
      { value: '24/7', label: 'coverage with the same voice for every office' },
      { value: 'English and Spanish', label: 'detected automatically' },
      { value: '1,000', label: 'AI minutes included per office each month' },
    ],
    features: [
      { title: 'Books into the schedule', detail: 'Follows block scheduling and provider rules.' },
      {
        title: 'Answers common questions',
        detail: 'Hours, insurance, parking and first-visit forms.',
      },
      { title: 'Emergency escalation', detail: 'Transfers to on-call with a summary text.' },
      { title: 'Summaries and transcripts', detail: 'Every call is searchable in Communications.' },
      { title: 'Handoff rules', detail: 'Decide what always goes to a person.' },
      {
        title: 'Runs on local AI',
        detail: 'Speech and language models stay inside Velora in HIPAA mode.',
      },
    ],
    steps: [
      { title: 'Forward overflow', detail: 'Set ring-over and after-hours forwarding in Phones.' },
      { title: 'Review the office brief', detail: 'Prefilled from your settings; edit the rest.' },
      { title: 'Listen to test calls', detail: 'Approve the voice and go live per office.' },
    ],
    setup: 'About 20 minutes per office, including test calls.',
    requires: ['Calls routed through Velora Phones'],
    usage: {
      meterKey: 'ai_minutes',
      label: 'AI minutes',
      unit: 'minute',
      includedPerOffice: 1000,
      overageMilliCents: 1300,
    },
    dataAccess: [
      W(
        'Schedule and open slots',
        'read_write',
        'Creates appointments, or queues them for staff where the PMS cannot',
        ['appointment.create'],
      ),
      W('Patients', 'read_write', 'Creates new patient records', ['patient.create']),
      W('Insurance carriers', 'read', 'Accepted carriers list'),
      W('Call recordings', 'processed', 'Kept in Velora for 90 days'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/receptionist',
    lockedPitch: 'Answer every overflow and after-hours call and book it straight into open time.',
  },
  {
    key: 'call_intelligence',
    name: 'Call Intelligence',
    shortName: 'Call Intelligence',
    by: 'Velora',
    category: 'calls',
    tone: 'green',
    icon: 'activity',
    ...midpoint(79, 99),
    bundleOf: [],
    trialDays: 14,
    badges: ['ai'],
    valueLine: 'Transcribes and scores every call so you can see which should have booked.',
    description:
      'Call Intelligence listens to recorded calls and flags the ones that mattered: new patients who did not book, treatment questions that went nowhere and billing calls that ended upset. Each office gets a weekly missed-opportunity list with one-click call-backs, and managers coach from real examples.',
    facts: [
      { value: 'Every call', label: 'transcribed on Velora’s local speech models' },
      { value: 'Weekly', label: 'missed-opportunity list per office' },
      { value: '4,000', label: 'transcribed minutes included per office' },
    ],
    features: [
      { title: 'Opportunity scoring', detail: 'New patient, treatment, recall and billing.' },
      { title: 'Call pop', detail: 'Patient context the moment the phone rings.' },
      { title: 'Call QA', detail: 'Score calls against your checklist and coach from clips.' },
      { title: 'Weekly digest', detail: 'Per office, every Monday morning.' },
    ],
    steps: [
      { title: 'Route calls through Phones', detail: 'Calls must flow through Velora Phones.' },
      { title: 'Pick scoring categories', detail: 'The defaults suit most general practices.' },
      { title: 'Review the first digest', detail: 'Arrives the Monday after you turn it on.' },
    ],
    setup: 'Live in about 5 minutes once Phones is connected.',
    requires: ['Calls routed through Velora Phones'],
    usage: {
      meterKey: 'transcribed_minutes',
      label: 'Transcribed minutes',
      unit: 'minute',
      includedPerOffice: 4000,
      overageMilliCents: 100,
    },
    dataAccess: [
      W('Call recordings', 'processed', 'Processed in Velora, never exported'),
      W('Patients', 'read', 'Caller ID matching'),
      W('Appointments', 'read', 'Did the caller book?'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/phones',
    lockedPitch: 'See which calls should have become appointments, ranked by what they were worth.',
  },
  {
    key: 'ai_scribe',
    name: 'AI Scribe + Voice Perio',
    shortName: 'AI Scribe',
    by: 'Velora',
    category: 'chart',
    tone: 'red',
    icon: 'stethoscope',
    ...midpoint(149, 249, 'module_usage'),
    bundleOf: [],
    trialDays: 14,
    badges: ['ai', 'popular', 'writeback'],
    valueLine: 'Drafts the clinical note and perio readings from the chairside conversation.',
    description:
      'AI Scribe turns the conversation in the chair into a structured clinical note: chief complaint, findings by tooth, treatment discussed, consent and next visit. In hygiene it captures perio readings spoken aloud straight into the perio chart. Nothing reaches the record until the provider reviews and signs, and audio is deleted after signing.',
    facts: [
      { value: 'Provider signs', label: 'nothing is saved to the record before review' },
      { value: '0 audio', label: 'kept after the note is signed' },
      { value: '2,500', label: 'scribe minutes included per office' },
    ],
    features: [
      { title: 'Ambient notes', detail: 'SOAP or your own template, by procedure.' },
      { title: 'Voice perio charting', detail: 'Pocket depths, bleeding, recession and mobility.' },
      { title: 'CDT suggestions', detail: 'Codes from what was said and done.' },
      { title: 'Sign and lock', detail: 'The signed note writes back to the PMS.' },
      {
        title: 'Treatment-plan PDF',
        detail: 'A patient-ready plan drafted from the signed note.',
      },
    ],
    steps: [
      { title: 'Pair a microphone', detail: 'Any USB microphone in the operatory.' },
      { title: 'Choose note templates', detail: 'Start from the defaults per procedure.' },
      { title: 'Sign your first note', detail: 'Review, edit and sign from Charting.' },
    ],
    setup: 'Live in about 5 minutes. Needs a microphone and Chrome or Edge.',
    requires: ['A microphone in each operatory'],
    usage: {
      meterKey: 'scribe_minutes',
      label: 'Scribe minutes',
      unit: 'minute',
      includedPerOffice: 2500,
      overageMilliCents: 500,
    },
    dataAccess: [
      W('Clinical notes', 'write', 'Only after the provider signs', ['note.add']),
      W('Perio exams', 'write', 'Readings the provider accepts'),
      W('Procedures and codes', 'read', 'Suggestions only'),
      W('Operatory audio', 'processed', 'Processed in Velora, deleted at signing'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/charting',
    lockedPitch:
      'Let the note write itself from the chairside conversation, ready for the provider to sign.',
  },
  {
    key: 'analytics_pro',
    name: 'Analytics Pro',
    shortName: 'Analytics Pro',
    by: 'Velora',
    category: 'numbers',
    tone: 'green',
    icon: 'bar-chart-3',
    ...midpoint(99, 149),
    bundleOf: [],
    trialDays: 14,
    badges: [],
    valueLine: 'Provider scorecards, goal pacing and office-to-office benchmarks.',
    description:
      'Analytics Pro adds the reports owners ask for: production per hour by provider, case acceptance by treatment type, hygiene reappointment and side-by-side office benchmarks. Set monthly goals per office and provider and read a short morning brief of what moved and why. Every number opens its formula and the rows behind it.',
    facts: [
      { value: 'Every office', label: 'compared side by side, plus the combined total' },
      { value: 'Goals', label: 'per office and per provider with daily pacing' },
      { value: 'Formulas', label: 'behind every number, with the rows' },
    ],
    features: [
      { title: 'Provider scorecards', detail: 'Production, collection and acceptance per hour.' },
      { title: 'Goals and pacing', detail: 'Daily pace against monthly goals.' },
      { title: 'Office benchmarks', detail: 'All offices side by side.' },
      { title: 'Weekly owner brief', detail: 'An AI summary of what moved and why, every Monday.' },
      {
        title: 'Patient grade',
        detail: 'A-F grade per patient from visits, payments and treatment.',
      },
      { title: 'Scheduled exports', detail: 'CSV or PDF to the owner every Monday.' },
    ],
    steps: [
      { title: 'Set goals', detail: 'Suggested from your last 12 months.' },
      { title: 'Pick report recipients', detail: 'Owner, office managers and providers.' },
      { title: 'Read the morning brief', detail: 'In Velora and by email.' },
    ],
    setup: 'Live immediately. Goals take about 10 minutes.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Production and collections', 'read', ''),
      W('Appointments', 'read', ''),
      W('Treatment plans', 'read', ''),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/analytics',
    lockedPitch:
      'Provider scorecards, goal pacing and office benchmarks, with every formula one click away.',
  },
  {
    key: 'imaging_ai',
    name: 'Imaging AI',
    shortName: 'Imaging AI',
    by: 'Velora',
    category: 'chart',
    tone: 'charcoal',
    icon: 'scan-line',
    ...midpoint(129, 179),
    bundleOf: [],
    trialDays: 14,
    badges: ['ai', 'new'],
    valueLine: 'Second-opinion overlays on bitewings and periapicals, accepted by the provider.',
    description:
      'Imaging AI reviews each new bitewing and periapical as it arrives and outlines likely caries, calculus, open margins and bone loss, with bone levels in millimetres. Findings are suggestions in Imaging and Charting: the provider accepts or dismisses each one, and nothing charts itself. It is an image-review aid, not a diagnosis.',
    facts: [
      { value: 'Suggestions', label: 'nothing charts without the provider' },
      { value: 'Bone level', label: 'measured in millimetres on BWX and PA' },
      { value: 'In Velora', label: 'images are processed inside Velora' },
    ],
    features: [
      { title: 'Caries and calculus', detail: 'Interproximal and occlusal findings.' },
      { title: 'Bone level in mm', detail: 'Measured on every BWX and PA.' },
      { title: 'Accept or dismiss', detail: 'Each finding needs a provider decision.' },
      { title: 'Patient view', detail: 'Clear overlays for treatment presentation.' },
      {
        title: 'Treatment-plan PDF',
        detail: 'Findings the provider accepted, ready to hand over.',
      },
    ],
    steps: [
      {
        title: 'Images flowing into Imaging',
        detail: 'Works with images already in Velora Imaging.',
      },
      { title: 'Choose which findings to show', detail: 'Turn categories on or off per office.' },
      { title: 'Review on the next BWX', detail: 'Overlays appear within seconds.' },
    ],
    setup: 'Live in about 5 minutes once Imaging receives images.',
    requires: ['Images in Velora Imaging'],
    usage: null,
    dataAccess: [
      W('Radiographs', 'processed', 'Processed in Velora'),
      W('Charted conditions', 'write', 'Only after the provider accepts', ['procedure.create']),
      W('Patient age and tooth chart', 'read', ''),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/imaging',
    lockedPitch:
      'A second look at every bitewing, with findings the provider accepts or dismisses.',
  },
  {
    key: 'imaging_3d',
    name: '3D Viewer (CBCT)',
    shortName: '3D Viewer',
    by: 'Velora',
    category: 'chart',
    tone: 'charcoal',
    icon: 'box',
    // Placeholder price for the owner to confirm (catalog copy only; no billing logic here).
    ...unranged(99),
    bundleOf: [],
    trialDays: 14,
    badges: ['new'],
    valueLine: "Open CBCT scans in Imaging, beside the patient's films.",
    description:
      'CBCT volumes from the office scanner arrive through the Velora capture agent and open in Imaging as three linked planes (axial, sagittal, coronal) with crosshairs, window and level, zoom, pan and length measurement. Nothing to install on the workstation.',
    facts: [
      { value: 'MPR', label: 'axial, sagittal and coronal, linked' },
      { value: 'DICOM', label: 'stored as the scanner sent it' },
      { value: 'In Velora', label: 'volumes stay inside Velora' },
    ],
    features: [
      { title: 'Three linked planes', detail: 'Crosshairs keep the views in step.' },
      { title: 'Window and level', detail: 'Bone and soft-tissue presets.' },
      { title: 'Measure in mm', detail: 'Lengths on any plane.' },
      { title: 'Streams as it opens', detail: 'Slices load with a progress bar.' },
    ],
    steps: [
      { title: 'Pair the capture agent', detail: "It receives the scanner's DICOM export." },
      { title: 'Take a scan', detail: 'It files itself under the patient.' },
      { title: 'Open Imaging > 3D', detail: 'The newest volume opens first.' },
    ],
    setup: 'Live once the capture agent receives a scan.',
    requires: ['Velora capture agent at the office'],
    usage: null,
    dataAccess: [
      W('CBCT volumes', 'read', 'Stored in Velora'),
      W('Patients', 'read', 'To file scans'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/imaging',
    lockedPitch: 'Open CBCT volumes in three linked planes, right in Imaging.',
  },
  {
    key: 'imaging_scans',
    name: 'Scans & Models',
    shortName: 'Scans',
    by: 'Velora',
    category: 'chart',
    tone: 'charcoal',
    icon: 'box',
    // Placeholder price for the owner to confirm (catalog copy only; no billing logic here).
    ...unranged(79),
    bundleOf: [],
    trialDays: 14,
    badges: ['new'],
    valueLine: 'Intraoral scans and models in Imaging, ready for the lab.',
    description:
      'STL, PLY and OBJ scans from the intraoral scanner arrive through the Velora capture agent and open in Imaging: rotate, show the upper, lower and bite scans together or apart, and measure point to point in millimetres. Lab cases show the scans linked to them.',
    facts: [
      { value: 'STL PLY OBJ', label: 'as the scanner exports them' },
      { value: 'Colour', label: 'shown when the scan has it' },
      { value: 'In Velora', label: 'scans stay inside Velora' },
    ],
    features: [
      { title: 'Upper, lower, bite', detail: 'Toggle each part of a scan.' },
      { title: 'Measure in mm', detail: 'Point to point on the model.' },
      { title: 'Lab cases', detail: 'See the scans sent with each case.' },
      { title: 'Any scanner', detail: 'Anything that exports STL, PLY or OBJ.' },
    ],
    steps: [
      { title: 'Pair the capture agent', detail: "Point it at the scanner's export folder." },
      { title: 'Scan the patient', detail: 'The files land under the patient.' },
      { title: 'Open Imaging > Scans', detail: 'The newest scan opens first.' },
    ],
    setup: 'Live once the capture agent receives a scan.',
    requires: ['Velora capture agent at the office'],
    usage: null,
    dataAccess: [W('Scan meshes', 'read', 'Stored in Velora'), W('Lab cases', 'read', '')],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/imaging',
    lockedPitch: 'Open intraoral scans and models in Imaging, linked to their lab cases.',
  },
  {
    key: 'lightboard',
    name: 'Pulse Status',
    shortName: 'Pulse Status',
    by: 'Velora',
    category: 'office',
    tone: 'orange',
    icon: 'monitor',
    ...midpoint(29, 49),
    bundleOf: [],
    trialDays: 14,
    badges: ['desktop'],
    valueLine: 'A light board for every operatory: seated, doctor needed, exam ready, turnover.',
    description:
      'Pulse Status is the operatory light board for the whole office. Each room shows its state on a small desktop app or tablet, and anyone can call the doctor or hygienist with one tap. Status changes also appear on the Schedule flow board, so the front desk knows which rooms are turning over without walking back.',
    facts: [
      { value: '1 tap', label: 'to call the doctor or hygienist' },
      { value: 'Live', label: 'room states on the Schedule flow board' },
      { value: 'No PHI', label: 'room screens can run in privacy mode' },
    ],
    features: [
      { title: 'Room states', detail: 'Seated, needs doctor, exam ready, turnover and open.' },
      { title: 'Desktop and tablet', detail: 'Windows desktop app and tablet pairing.' },
      { title: 'Flow board sync', detail: 'Status shows on Schedule in real time.' },
      { title: 'Emergency page', detail: 'Office-wide alert to every screen.' },
    ],
    steps: [
      { title: 'Install on each operatory', detail: 'Desktop app or a paired tablet per room.' },
      { title: 'Join with the office key', detail: 'Each install joins its office automatically.' },
      { title: 'Name the rooms', detail: 'Matches your operatories in Schedule.' },
    ],
    setup: 'About 2 minutes per operatory.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Operatories', 'read', ''),
      W('Today’s appointments', 'read', 'Flow board only'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/lightboard',
    lockedPitch: 'Every room at a glance, and the doctor one tap away.',
  },
  {
    key: 'radio',
    name: 'Pulse Radio',
    shortName: 'Pulse Radio',
    by: 'Velora',
    category: 'office',
    tone: 'charcoal',
    icon: 'radio',
    ...midpoint(19, 29),
    bundleOf: [],
    trialDays: 14,
    badges: ['beta', 'desktop'],
    valueLine: 'Background music and overhead announcements for every room, managed in one place.',
    description:
      'Pulse Radio plays background music in reception and operatories, with separate stations and volume per zone. The same player carries overhead announcements, so an emergency page from Pulse Status can reach every room. Managers pick stations per office from Velora and can quiet any room remotely.',
    facts: [
      { value: '3 zones', label: 'per office: reception, clinical, break room' },
      { value: '1 place', label: 'to manage every office' },
      { value: 'No PHI', label: 'Pulse Radio never reads patient data' },
    ],
    features: [
      { title: 'Zones and stations', detail: 'Reception, clinical and break room.' },
      { title: 'Announcements', detail: 'Pages and emergency alerts over the music.' },
      { title: 'Remote control', detail: 'Change stations or mute from Velora.' },
      { title: 'Per-office players', detail: 'Installers pre-keyed to each office.' },
    ],
    steps: [
      { title: 'Install the player', detail: 'One per zone.' },
      {
        title: 'Join with the office invite',
        detail: 'The installer is pre-keyed to your office.',
      },
      { title: 'Pick stations', detail: 'Per zone, per office.' },
    ],
    setup: 'About 5 minutes per zone. Music licensing is confirmed before general release.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Office and zone names', 'read', ''),
      W('Patient data', 'none', 'Pulse Radio never reads PHI'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/radio',
    lockedPitch: 'Music and announcements for every room, managed from one place.',
  },
  {
    key: 'online_booking',
    name: 'Online Booking + DentChat',
    shortName: 'Online Booking',
    by: 'Velora',
    category: 'fill',
    tone: 'green',
    icon: 'calendar-plus',
    ...midpoint(75, 119),
    bundleOf: [],
    trialDays: 14,
    badges: ['writeback'],
    valueLine: 'Real-time booking on your website, plus a chat that turns visitors into patients.',
    description:
      'Online Booking shows patients only the openings you want filled, using your scheduling rules, and books straight into the schedule or into an approval queue where your PMS cannot take the write. DentChat sits on the same page, answers questions, collects insurance details and hands leads to the front desk inbox. New patients get intake forms the moment they book.',
    facts: [
      { value: '3 screens', label: 'from reason to booked' },
      { value: 'Rules-aware', label: 'only the openings you choose to fill' },
      { value: '1 line', label: 'of code, or a hosted booking page' },
    ],
    features: [
      { title: 'Rules-aware openings', detail: 'New patient, hygiene and emergency slots only.' },
      { title: 'Website chat', detail: 'Answers questions and captures leads.' },
      { title: 'Intake forms on booking', detail: 'Sent automatically and resumable on a phone.' },
      { title: 'Source tracking', detail: 'Google, website, social and referral.' },
    ],
    steps: [
      { title: 'Pick bookable reasons', detail: 'And which providers and slots they can use.' },
      {
        title: 'Add it to your website',
        detail: 'Paste one line of code or share the hosted page.',
      },
      { title: 'Watch Leads', detail: 'New bookings and chats appear in Communications.' },
    ],
    setup: 'About 15 minutes, including your website.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Schedule and open slots', 'read_write', 'Creates appointments', ['appointment.create']),
      W('Patients', 'write', 'New patient records', ['patient.create']),
      W('Insurance', 'write', 'Carrier and member ID from chat'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/booking',
    lockedPitch: 'Let patients book the openings you want filled, straight from your website.',
  },
  {
    key: 'membership',
    name: 'Membership Plans',
    shortName: 'Membership Plans',
    by: 'Velora',
    category: 'paid',
    tone: 'green',
    icon: 'badge-check',
    ...midpoint(49, 99),
    bundleOf: [],
    trialDays: 14,
    badges: ['new'],
    valueLine: 'In-house plans for uninsured patients, billed monthly with included visits.',
    description:
      'Membership Plans lets each office sell its own in-house plan to patients without insurance: two cleanings, exams and x-rays a year plus a discount on treatment, billed monthly or yearly. Velora tracks the included services, applies the discount on the ledger and shows members on the schedule.',
    facts: [
      { value: 'Per office', label: 'plans, prices and included services' },
      { value: 'Counters', label: 'for included visits on every member' },
      { value: 'Monthly or yearly', label: 'patient billing' },
    ],
    features: [
      { title: 'Plan builder', detail: 'Included services, discount and price per office.' },
      { title: 'Recurring billing', detail: 'Card on file, monthly or yearly.' },
      { title: 'Included-service counters', detail: 'What each member has used this year.' },
      { title: 'Member badges', detail: 'On the schedule, chart and ledger.' },
    ],
    steps: [
      { title: 'Build your plans', detail: 'Start from the adult and child templates.' },
      { title: 'Enrol members', detail: 'From the patient record or at checkout.' },
      { title: 'Track renewals', detail: 'Renewals and lapsed members in one list.' },
    ],
    setup: 'About 15 minutes to build the first plan.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Patients', 'read', 'Member status'),
      W('Procedures', 'read', 'Included-service counters'),
      W(
        'Ledger',
        'write',
        'Membership fees and discounts post in Velora; PMS posting waits for PulseSync v2',
        [],
      ),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/membership',
    lockedPitch: 'Turn uninsured patients into members with an in-house plan billed monthly.',
  },
  {
    key: 'ai_assistant',
    name: 'Ask Velora',
    shortName: 'Ask Velora',
    by: 'Velora',
    category: 'numbers',
    tone: 'violet',
    icon: 'sparkles',
    ...unranged(49),
    bundleOf: [],
    trialDays: 14,
    badges: ['ai'],
    valueLine: 'Ask about your practice in plain words and get the answer with its rows.',
    description:
      'Ask Velora answers questions about your practice in plain words: who is overdue for hygiene, what was produced last week by provider, which claims are aging. Every answer shows the query it ran and the rows behind it, and any action it suggests waits for your confirmation.',
    facts: [
      { value: 'Read-only', label: 'questions run as read-only practice queries' },
      { value: 'Shows its work', label: 'the query and rows behind every answer' },
      { value: 'Your AI setup', label: 'answers come from the AI service your practice approved' },
    ],
    features: [
      { title: 'Plain-word questions', detail: 'Schedule, patients, production and claims.' },
      { title: 'Answers with rows', detail: 'Open the rows behind any answer.' },
      { title: 'Suggested actions', detail: 'Turn an answer into a task, with your confirmation.' },
      { title: 'Command palette', detail: 'Ask from anywhere with the palette.' },
    ],
    steps: [
      { title: 'Open Ask Velora', detail: 'From the side nav or the command palette.' },
      { title: 'Ask a question', detail: 'Start from the suggested questions.' },
      { title: 'Act on the answer', detail: 'Confirm any action it proposes.' },
    ],
    setup: 'Live immediately.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Schedule and patients', 'read', 'Read-only queries'),
      W('Production and claims', 'read', 'Read-only queries'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/assistant',
    lockedPitch: 'Ask about your practice in plain words and see the rows behind every answer.',
  },
  {
    key: 'team',
    name: 'Team Timeclock',
    shortName: 'Team',
    by: 'Velora',
    category: 'office',
    tone: 'charcoal',
    icon: 'clock',
    ...midpoint(29, 49),
    bundleOf: [],
    trialDays: 14,
    badges: ['new'],
    valueLine: 'Clock in with a PIN at any workstation and send hours to Square payroll.',
    description:
      'Team Timeclock lets staff clock in and out with their own PIN from any Velora workstation, with breaks and edits a manager approves. Hours roll up per pay period and office, and approved timesheets sync to Square payroll so nobody retypes them.',
    facts: [
      { value: 'PIN', label: 'clock in from any workstation' },
      { value: 'Per office', label: 'hours and pay periods' },
      { value: 'Square payroll', label: 'approved hours sync' },
    ],
    features: [
      { title: 'PIN clock-in', detail: 'From the top bar of any workstation.' },
      { title: 'Breaks and edits', detail: 'Edits need a manager to approve.' },
      { title: 'Pay-period totals', detail: 'Regular and overtime hours per person.' },
      { title: 'Square payroll sync', detail: 'Approved timesheets go to Square.' },
    ],
    steps: [
      { title: 'Set staff PINs', detail: 'Each person sets their own.' },
      { title: 'Pick the pay period', detail: 'Weekly, every two weeks or twice a month.' },
      { title: 'Connect Square payroll', detail: 'Optional; hours export as CSV otherwise.' },
    ],
    setup: 'About 10 minutes, plus Square payroll if you use it.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Staff and roles', 'read', 'Who can clock in'),
      W('Timesheets', 'read_write', 'Kept in Velora; approved hours go to Square payroll'),
      W('Patient data', 'none', 'The timeclock never reads PHI'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: null,
    lockedPitch: 'Clock in with a PIN at any workstation and send approved hours to payroll.',
  },
  {
    key: 'compliance',
    name: 'Compliance',
    shortName: 'Compliance',
    by: 'Velora',
    category: 'office',
    tone: 'green',
    icon: 'shield-check',
    ...midpoint(49, 79),
    bundleOf: [],
    trialDays: 14,
    badges: ['new'],
    valueLine: 'HIPAA evidence, sterilization and OSHA logs, ready for an inspector.',
    description:
      'Compliance keeps the evidence an inspector asks for in one place: the HIPAA risk assessment and policies, staff training records, sterilizer spore tests and cycle logs, and OSHA checklists. Due items show per office, and each log exports as a PDF with who did what and when.',
    facts: [
      { value: 'One place', label: 'for HIPAA, sterilization and OSHA evidence' },
      { value: 'Per office', label: 'logs and due dates' },
      { value: 'PDF', label: 'export of every log' },
    ],
    features: [
      { title: 'HIPAA evidence hub', detail: 'Risk assessment, policies and training records.' },
      { title: 'Sterilization logs', detail: 'Cycles and weekly spore tests per sterilizer.' },
      { title: 'OSHA checklists', detail: 'Annual and monthly items with due dates.' },
      { title: 'Inspector export', detail: 'A dated PDF of every log.' },
    ],
    steps: [
      { title: 'Add your sterilizers', detail: 'One per unit, per office.' },
      { title: 'Upload current policies', detail: 'Or start from the templates.' },
      { title: 'Assign training', detail: 'Due dates per person.' },
    ],
    setup: 'About 20 minutes per office.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Staff', 'read', 'Training records'),
      W('Compliance logs', 'read_write', 'Kept in Velora'),
      W('Patient data', 'none', 'Compliance never reads PHI'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: null,
    lockedPitch: 'Keep HIPAA, sterilization and OSHA evidence ready for any inspection.',
  },
  {
    key: 'tablet_checkin',
    name: 'Tablet Check-in',
    shortName: 'Tablet Check-in',
    by: 'Velora',
    category: 'office',
    tone: 'violet',
    icon: 'tablet',
    ...midpoint(39, 59),
    bundleOf: [],
    trialDays: 14,
    badges: ['new', 'writeback'],
    valueLine: 'Patients check in and finish paperwork on a tablet; cards are read for you.',
    description:
      'Tablet Check-in hands the patient a tablet at the front desk: they confirm their details, sign consents and finish the health history, and the insurance card and ID are photographed and read into the record. Web forms and the patient portal stay free; this adds the in-office kiosk and card reading.',
    facts: [
      { value: 'Kiosk', label: 'mode locks the tablet to check-in' },
      { value: 'Card reading', label: 'insurance card and ID read into the record' },
      { value: 'Portal forms', label: 'stay free in the base platform' },
    ],
    features: [
      { title: 'Kiosk check-in', detail: 'Arrival, details and signatures on one tablet.' },
      { title: 'Insurance card reading', detail: 'Front and back read into insurance fields.' },
      { title: 'Health history', detail: 'Updated by the patient, reviewed by staff.' },
      { title: 'Arrived status', detail: 'Marks the appointment arrived.' },
    ],
    steps: [
      { title: 'Pair a tablet', detail: 'Any iPad or Android tablet with a browser.' },
      { title: 'Pick the forms', detail: 'From your portal forms.' },
      { title: 'Hand it over', detail: 'Patients start from their name and birth date.' },
    ],
    setup: 'About 5 minutes per tablet.',
    requires: ['A tablet at the front desk'],
    usage: null,
    dataAccess: [
      W('Patients', 'read_write', 'Contact details the patient confirms', ['patient.update']),
      W('Insurance', 'write', 'Fields read from the card, reviewed by staff'),
      W('Appointments', 'read_write', 'Arrived status', ['appointment.update']),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: null,
    lockedPitch: 'Let patients check in and finish paperwork on a tablet at the front desk.',
  },
  {
    key: 'ppo_analysis',
    name: 'PPO Analysis',
    shortName: 'PPO Analysis',
    by: 'Velora',
    category: 'paid',
    tone: 'red',
    icon: 'scale',
    ...midpoint(99, 99, 'module', { oneTime: { label: 'One-time PPO report', cents: 39_900 } }),
    bundleOf: [],
    trialDays: 14,
    badges: ['new'],
    valueLine: 'See what each PPO really pays against your fees, and which to renegotiate.',
    description:
      'PPO Analysis reads your own claims and EOBs to show what each carrier actually pays per procedure against your full fee, the write-off by plan and the patients behind it. It ranks plans by what dropping or renegotiating would mean, the work a consultant would otherwise bill for.',
    facts: [
      { value: 'Your claims', label: 'the analysis uses your own paid claims' },
      { value: 'Per carrier', label: 'paid vs full fee by procedure' },
      { value: 'Monthly or once', label: 'subscribe, or buy one report' },
    ],
    features: [
      { title: 'Paid vs fee', detail: 'Per carrier and per procedure.' },
      { title: 'Write-off by plan', detail: 'What each plan costs you a year.' },
      { title: 'Drop scenarios', detail: 'Patients and production at risk per plan.' },
      { title: 'Renegotiation sheet', detail: 'The codes worth asking about first.' },
    ],
    steps: [
      { title: 'Let claims sync', detail: 'Twelve months of paid claims work best.' },
      { title: 'Read the report', detail: 'Ranked by write-off.' },
      { title: 'Act on it', detail: 'Export the renegotiation sheet.' },
    ],
    setup: 'Live once twelve months of paid claims have synced.',
    requires: ['Paid claims in Velora'],
    usage: null,
    dataAccess: [
      W('Claims and EOBs', 'read', 'Paid amounts per procedure'),
      W('Fee schedules', 'read', 'Full fees'),
      W('Patients', 'read', 'Counts per plan'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/insurance-fees/ppo-analysis',
    lockedPitch: 'See what each PPO really pays and which plans are worth renegotiating.',
  },
  {
    key: 'websites',
    name: 'Websites + SEO',
    shortName: 'Websites',
    by: 'Velora',
    category: 'fill',
    tone: 'orange',
    icon: 'globe',
    ...midpoint(199, 399, 'service', { setupFeeCents: 100_000 }),
    bundleOf: [],
    trialDays: 0,
    badges: ['new'],
    valueLine: 'A fast practice website with local SEO, built and kept up for you.',
    description:
      'Websites + SEO is a done-for-you service: a fast, accessible site per office with your providers, services and reviews, online booking built in, and monthly local SEO work on your Google profiles. The one-time setup covers design and content; the monthly price covers hosting, updates and SEO.',
    facts: [
      { value: 'Done for you', label: 'design, content and monthly SEO' },
      { value: 'Per office', label: 'pages and Google profiles' },
      { value: 'Setup fee', label: 'one time, then monthly' },
    ],
    features: [
      { title: 'Practice website', detail: 'Providers, services, insurance and reviews.' },
      { title: 'Booking built in', detail: 'Works with Online Booking when it is on.' },
      { title: 'Local SEO', detail: 'Google profile posts and citations every month.' },
      { title: 'Monthly report', detail: 'Visits, calls and bookings from the site.' },
    ],
    steps: [
      { title: 'Kick-off call', detail: 'About 30 minutes with our team.' },
      { title: 'Review the draft', detail: 'Usually within two weeks.' },
      { title: 'Go live', detail: 'We move your domain for you.' },
    ],
    setup: 'About two weeks from kick-off to launch.',
    requires: [],
    usage: null,
    dataAccess: [
      W('Office and provider details', 'read', 'Shown on the site'),
      W('Patient data', 'none', 'The website never reads PHI'),
    ],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: null,
    lockedPitch: 'A fast practice website with local SEO, built and kept up for you.',
  },
  {
    key: 'engage',
    name: 'Patient Service package',
    shortName: 'Patient Service',
    by: 'Velora',
    category: 'talk',
    tone: 'orange',
    icon: 'message-square',
    priceCents: 24_900,
    pricing: {
      class: 'bundle',
      rangeCents: null,
      placeholder: true,
      setupFeeCents: null,
      oneTime: null,
    },
    bundleOf: ['texting', 'reviews', 'call_intelligence', 'tablet_checkin'],
    trialDays: 0,
    badges: ['popular'],
    valueLine:
      'Every patient answered: texting, missed-call text back, reviews, forms and call analytics.',
    description:
      'Patient Service is everything that touches a patient between visits. Two-way texting from the office number with reminders, confirmations and a text back for every missed call; a Google review request after each visit; forms and check-in on a tablet; and call analytics that show which calls should have booked. Each part works exactly as it does on its own.',
    facts: [
      { value: '4 modules', label: 'Texting, Reviews, Call Intelligence and Tablet Check-in' },
      { value: 'One price', label: 'per office per month' },
      { value: 'Same allowances', label: 'texts and transcribed minutes as on their own' },
    ],
    features: [
      {
        title: 'Warp 9 Texting',
        detail: 'Two-way texting, reminders, confirmations and missed-call text back.',
      },
      { title: 'Reviews', detail: 'A Google review request after every visit, no gating.' },
      { title: 'Forms and check-in', detail: 'Paperwork and check-in on a tablet.' },
      { title: 'Call Intelligence', detail: 'Every call transcribed and scored.' },
    ],
    steps: [
      { title: 'Turn it on', detail: 'For one office or all of them.' },
      { title: 'Set up each part', detail: 'The same steps as on their own.' },
      { title: 'Turn off singles', detail: 'Modules you already pay for get a credit.' },
    ],
    setup: 'Each part sets up as it does on its own.',
    requires: ['Calls routed through Velora Phones (call analytics)'],
    usage: null,
    dataAccess: [],
    phiLeavesVelora: true,
    baaCovered: true,
    appPath: '/communications',
    lockedPitch: 'Texting, reviews, forms and call analytics together for less.',
  },
  {
    key: 'ai_suite',
    name: 'AI Suite package',
    shortName: 'AI Suite',
    by: 'Velora',
    category: 'chart',
    tone: 'violet',
    icon: 'sparkles',
    priceCents: 49_900,
    pricing: {
      class: 'bundle',
      rangeCents: null,
      placeholder: true,
      setupFeeCents: null,
      oneTime: null,
    },
    bundleOf: ['ai_receptionist', 'ai_scribe', 'imaging_ai', 'ai_assistant'],
    trialDays: 0,
    badges: ['ai'],
    valueLine:
      'Calls answered after hours, notes drafted chairside, a second look at every film, and answers in plain words.',
    description:
      'AI Suite brings Velora’s AI together: the Digital Receptionist for overflow and after-hours calls, AI Scribe with voice perio charting, Imaging AI on every bitewing and periapical, and Ask Velora for questions about the office in plain words. Each keeps its own allowance and works exactly as it does alone; the provider signs every note and decides every finding.',
    facts: [
      { value: '4 modules', label: 'Receptionist, Scribe, Imaging AI and Ask Velora' },
      { value: 'One price', label: 'per office per month' },
      { value: 'Same allowances', label: 'AI and scribe minutes as on their own' },
    ],
    features: [
      { title: 'Digital Receptionist', detail: 'Answers overflow and after-hours calls.' },
      { title: 'AI Scribe + Voice Perio', detail: 'Notes and perio from the conversation.' },
      { title: 'Imaging AI', detail: 'Second-look overlays the provider accepts.' },
      { title: 'Ask Velora', detail: 'Questions about patients and numbers, answered.' },
    ],
    steps: [
      { title: 'Turn it on', detail: 'For one office or all of them.' },
      { title: 'Set up each part', detail: 'The same steps as on their own.' },
      { title: 'Turn off singles', detail: 'Modules you already pay for get a credit.' },
    ],
    setup: 'Each part sets up as it does on its own.',
    requires: ['Calls routed through Velora Phones', 'A microphone in each operatory'],
    usage: null,
    dataAccess: [],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: null,
    lockedPitch: 'Velora’s AI on one price.',
  },
  {
    key: 'production',
    name: 'Production package',
    shortName: 'Production',
    by: 'Velora',
    category: 'fill',
    tone: 'green',
    icon: 'trending-up',
    // Placeholder price for the owner to confirm (catalog copy only; no billing logic here).
    priceCents: 14_900,
    pricing: {
      class: 'bundle',
      rangeCents: null,
      placeholder: true,
      setupFeeCents: null,
      oneTime: null,
    },
    bundleOf: ['online_booking', 'membership'],
    trialDays: 0,
    badges: ['new'],
    valueLine:
      'Fill open chair time and get more treatment accepted: recall, unscheduled treatment, gap fill and case acceptance.',
    description:
      'Production is for doing more of the dentistry already waiting. It works the recall and unscheduled-treatment lists, offers open chair time to the right patients first (Gap Fill), lets patients book themselves online, and closes cases with clear treatment plans, financing options and an in-house membership plan for patients without insurance. Production is measured on completed procedures, never on scheduled ones.',
    facts: [
      { value: 'Lists that act', label: 'recall, unscheduled treatment and open time' },
      { value: 'Case acceptance', label: 'plans, options and membership in one place' },
      { value: 'Completed work', label: 'what the package is measured on' },
    ],
    features: [
      { title: 'Recall', detail: 'Who is due and overdue, ready to call or text.' },
      {
        title: 'Unscheduled treatment',
        detail: 'Accepted work with no appointment, largest first.',
      },
      {
        title: 'Gap Fill',
        detail: 'Open chair time matched to the patients most likely to take it.',
      },
      { title: 'Treatment plans and case acceptance', detail: 'Options, financing and follow-up.' },
      { title: 'Online Booking', detail: 'Patients book into real openings on your rules.' },
      { title: 'Membership plans', detail: 'An in-house plan for patients without insurance.' },
    ],
    steps: [
      { title: 'Turn it on', detail: 'For one office or all of them.' },
      { title: 'Review the lists', detail: 'Recall and unscheduled treatment fill from the PMS.' },
      { title: 'Work them daily', detail: 'The morning huddle shows what to call first.' },
    ],
    setup: 'Live once the schedule and treatment plans have synced.',
    requires: [],
    usage: null,
    dataAccess: [],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/schedule',
    lockedPitch: 'Fill open chair time and get more treatment accepted.',
  },
  {
    key: 'analytics_suite',
    name: 'Analytics package',
    shortName: 'Analytics',
    by: 'Velora',
    category: 'numbers',
    tone: 'charcoal',
    icon: 'bar-chart-3',
    // Placeholder price for the owner to confirm (catalog copy only; no billing logic here).
    priceCents: 17_900,
    pricing: {
      class: 'bundle',
      rangeCents: null,
      placeholder: true,
      setupFeeCents: null,
      oneTime: null,
    },
    bundleOf: ['analytics_pro', 'ppo_analysis'],
    trialDays: 0,
    badges: [],
    valueLine: 'Know your numbers: provider scorecards, goals, office benchmarks and PPO analysis.',
    description:
      'Analytics is for owners who want the numbers without changing how the front desk works. Provider scorecards, goal pacing and office benchmarks with every formula one click away, plus PPO Analysis: what each plan allows against your full fees by category, the patients on each plan and what leaving one could mean.',
    facts: [
      { value: '2 modules', label: 'Analytics Pro and PPO Analysis' },
      { value: 'Read only', label: 'nothing is written to the PMS' },
      { value: 'Formulas', label: 'behind every number' },
    ],
    features: [
      { title: 'Provider scorecards', detail: 'Production, collection and acceptance per hour.' },
      { title: 'Goals and benchmarks', detail: 'Pacing per office and provider, side by side.' },
      { title: 'PPO Analysis', detail: 'Discount vs UCR by category, patients per plan.' },
      { title: 'Renegotiation pack', detail: 'The codes worth asking each payer about first.' },
    ],
    steps: [
      { title: 'Turn it on', detail: 'For one office or all of them.' },
      { title: 'Set goals', detail: 'Suggested from your last 12 months.' },
      { title: 'Read the PPO analysis', detail: 'Ranked by write-off.' },
    ],
    setup: 'Live immediately.',
    requires: [],
    usage: null,
    dataAccess: [],
    phiLeavesVelora: false,
    baaCovered: true,
    appPath: '/analytics',
    lockedPitch: 'Scorecards, goals, benchmarks and PPO analysis on one price.',
  },
];

export const MARKETPLACE_CATALOG_BY_KEY: Readonly<Record<MarketplaceModuleKey, CatalogModule>> =
  Object.fromEntries(MARKETPLACE_CATALOG.map((m) => [m.key, m])) as Record<
    MarketplaceModuleKey,
    CatalogModule
  >;

/* ------------------------------------------------------------------------ */
/* Bundles, annual prepay, usage meters and terms                            */
/* ------------------------------------------------------------------------ */

/** Catalog entries that are bundles. */
export const MARKETPLACE_BUNDLES: readonly CatalogModule[] = MARKETPLACE_CATALOG.filter(
  (m) => m.bundleOf.length > 0,
);

/** Catalog entries that are single modules (not bundles). */
export const MARKETPLACE_MODULES: readonly CatalogModule[] = MARKETPLACE_CATALOG.filter(
  (m) => m.bundleOf.length === 0,
);

/** Bundle keys that grant `featureKey` (empty for a feature no bundle carries). */
export function bundlesGranting(featureKey: string): MarketplaceModuleKey[] {
  return MARKETPLACE_BUNDLES.filter((b) =>
    (b.bundleOf as readonly string[]).includes(featureKey),
  ).map((b) => b.key);
}

/** Feature keys a purchase of `key` grants: the bundle's members, or the module itself. */
export function grantsOf(key: MarketplaceModuleKey): MarketplaceModuleKey[] {
  const m = MARKETPLACE_CATALOG_BY_KEY[key];
  return m.bundleOf.length > 0 ? [...m.bundleOf] : [key];
}

/** What a bundle's members cost bought one by one, per office per month. */
export function alaCarteCents(bundle: CatalogModule): number {
  return bundle.bundleOf.reduce((sum, k) => sum + MARKETPLACE_CATALOG_BY_KEY[k].priceCents, 0);
}

/* ------------------------------------------------------------------------ */
/* Packages (owner feedback 2026-10-28)                                      */
/* ------------------------------------------------------------------------ */

/**
 * Packages: Dr. John said Velora has too many modules to choose from, so the
 * Marketplace leads with four outcome packages (each a bundle key above) and
 * keeps the single modules as "advanced". A package lists what it does for
 * the office; `includes` names its member modules and the core workflows it
 * builds on (marked `core`: those are in every Velora plan, the package
 * adds the modules that act on them). Prices are the bundle's placeholders.
 */
export const PackageIncludeSchema = z.object({
  label: z.string(),
  /** A member module of the bundle, or null for a core workflow it builds on. */
  moduleKey: MarketplaceModuleKeySchema.nullable(),
  /** In every Velora plan already (not what the package charges for). */
  core: z.boolean(),
});
export type PackageInclude = z.infer<typeof PackageIncludeSchema>;

export const MarketplacePackageSchema = z.object({
  key: MarketplaceModuleKeySchema,
  /** The outcome, as the card's headline. */
  outcome: z.string(),
  /** Who it is for, one line. */
  forWho: z.string(),
  includes: z.array(PackageIncludeSchema),
});
export type MarketplacePackage = z.infer<typeof MarketplacePackageSchema>;

const mod = (key: MarketplaceModuleKey, label: string): PackageInclude => ({
  label,
  moduleKey: key,
  core: false,
});
const core = (label: string): PackageInclude => ({ label, moduleKey: null, core: true });

export const MARKETPLACE_PACKAGES: readonly MarketplacePackage[] = [
  {
    key: 'production',
    outcome: 'Fill the chairs and get more treatment accepted',
    forWho: 'Offices with open time, overdue recall or treatment waiting to be scheduled.',
    includes: [
      core('Recall lists'),
      core('Unscheduled treatment'),
      core('Gap Fill for open chair time'),
      core('Treatment plans and case acceptance'),
      mod('online_booking', 'Online Booking'),
      mod('membership', 'Membership plans'),
    ],
  },
  {
    key: 'engage',
    outcome: 'Answer every patient, every time',
    forWho: 'Front desks that miss calls, chase confirmations or want more reviews.',
    includes: [
      mod('texting', 'Texting, reminders and missed-call text back'),
      mod('reviews', 'Reviews'),
      mod('tablet_checkin', 'Forms and tablet check-in'),
      mod('call_intelligence', 'Call analytics'),
    ],
  },
  {
    key: 'analytics_suite',
    outcome: 'Know your numbers, and what each PPO really costs',
    forWho: 'Owners who want scorecards and payer facts without changing the front desk.',
    includes: [
      mod('analytics_pro', 'Provider scorecards, goals and benchmarks'),
      mod('ppo_analysis', 'PPO analysis'),
      core('KPI dashboard and office compare'),
    ],
  },
  {
    key: 'ai_suite',
    outcome: 'Let AI take the calls, the notes and the second look',
    forWho: 'Practices ready to use AI for after-hours calls, charting and imaging.',
    includes: [
      mod('ai_receptionist', 'Digital Receptionist'),
      mod('ai_scribe', 'AI Scribe + Voice Perio'),
      mod('imaging_ai', 'Imaging AI'),
      mod('ai_assistant', 'Ask Velora'),
    ],
  },
];

/** Whether `key` is one of the Marketplace's packages. */
export function isPackageKey(key: string): boolean {
  return MARKETPLACE_PACKAGES.some((p) => p.key === key);
}

/** The single modules no package carries (the advanced list still shows every module). */
export function modulesOutsidePackages(): MarketplaceModuleKey[] {
  const inPkg = new Set(
    MARKETPLACE_PACKAGES.flatMap((p) => MARKETPLACE_CATALOG_BY_KEY[p.key].bundleOf),
  );
  return MARKETPLACE_MODULES.map((m) => m.key).filter((k) => !inPkg.has(k));
}

/**
 * Annual prepay (owner-research-marketplace.md section 3, after Atlassian):
 * pay for 10 months, get 12. Billed as one invoice; no monthly lines for that
 * module until the term ends, then it renews monthly unless renewed.
 */
export const ANNUAL_PREPAY = { paidMonths: 10, termMonths: 12 } as const;

export const BillingTermSchema = z.enum(['monthly', 'annual']);
export type BillingTerm = z.infer<typeof BillingTermSchema>;

/** The annual prepay price for a monthly price. */
export function annualPriceCents(monthlyCents: number): number {
  return monthlyCents * ANNUAL_PREPAY.paidMonths;
}

/**
 * Metered usage (owner-research-marketplace.md section 3). Every meter is a
 * catalog entry; `billed` meters already bill overage through their module
 * on the monthly invoice, the others are recorded only (no billing run uses
 * them until the owner sets the price and turns billing on).
 */
export const UsageMeterDefSchema = z.object({
  /** `usage_meter.feature_key`. */
  meterKey: z.string(),
  label: z.string(),
  unit: z.string(),
  /** The module whose allowance it counts against; null for a platform meter. */
  moduleKey: MarketplaceModuleKeySchema.nullable(),
  includedPerOffice: z.number().int().nonnegative(),
  /** Hundredths of a cent per unit above the allowance (0.03 USD = 300). */
  unitPriceMilliCents: z.number().int().nonnegative(),
  /** `per_unit` counts events; `per_month` counts things held in a month. */
  cadence: z.enum(['per_unit', 'per_month']),
  billed: z.boolean(),
  /** Research range of the unit price, hundredths of a cent. */
  rangeMilliCents: z.object({ min: z.number().int(), max: z.number().int() }),
  placeholder: z.boolean(),
});
export type UsageMeterDef = z.infer<typeof UsageMeterDefSchema>;

export const MARKETPLACE_USAGE_METERS: readonly UsageMeterDef[] = [
  {
    meterKey: 'sms',
    label: 'Texts',
    unit: 'text',
    moduleKey: 'texting',
    includedPerOffice: 2500,
    unitPriceMilliCents: 300,
    cadence: 'per_unit',
    billed: true,
    rangeMilliCents: { min: 200, max: 400 },
    placeholder: true,
  },
  {
    meterKey: 'ai_minutes',
    label: 'AI receptionist minutes',
    unit: 'minute',
    moduleKey: 'ai_receptionist',
    includedPerOffice: 1000,
    unitPriceMilliCents: 1300,
    cadence: 'per_unit',
    billed: true,
    rangeMilliCents: { min: 1000, max: 1500 },
    placeholder: true,
  },
  {
    meterKey: 'eligibility_checks',
    label: 'Eligibility checks',
    unit: 'check',
    moduleKey: null,
    includedPerOffice: 200,
    unitPriceMilliCents: 3800,
    cadence: 'per_unit',
    billed: false,
    rangeMilliCents: { min: 2500, max: 5000 },
    placeholder: true,
  },
  {
    meterKey: 'tracking_numbers',
    label: 'Tracking numbers',
    unit: 'number',
    moduleKey: null,
    includedPerOffice: 0,
    unitPriceMilliCents: 40_000,
    cadence: 'per_month',
    billed: false,
    rangeMilliCents: { min: 30_000, max: 50_000 },
    placeholder: true,
  },
];

/**
 * Module terms (owner-research-marketplace.md "Legal"): the Master
 * Subscription Agreement plus a short page per module. Accepted by checkbox
 * at checkout and stored with the version and time. Bump the version when
 * the text changes; checkout then asks again.
 */
export const MODULE_TERMS_VERSION = '2026-11-20';
export const MSA_VERSION = '2026-11-20';
/** Days after the first paid charge in which turning a module off refunds it in full. */
export const REFUND_WINDOW_DAYS = 30;

export interface ModuleTerms {
  version: string;
  /** Plain-language points shown above the checkbox. */
  points: string[];
}

const AI_LIMITS: Partial<Record<MarketplaceModuleKey, string>> = {
  imaging_ai: 'Imaging AI is an image-review aid, not a diagnosis. The provider decides.',
  ai_scribe: 'The provider must review and sign every note; nothing is saved before that.',
  ai_receptionist: 'The receptionist follows your rules and hands emergencies to a person.',
  ai_assistant: 'Answers come from your data; check them before acting on them.',
  ai_suite: 'Imaging AI is not a diagnosis, and the provider signs every scribe note.',
  texting:
    'Texts travel over carrier networks under the HIPAA conduit exception; keep clinical detail out of them.',
  engage:
    'Texts travel over carrier networks under the HIPAA conduit exception; keep clinical detail out of them.',
};

/** The module terms shown at checkout for `m`. */
export function moduleTerms(m: CatalogModule): ModuleTerms {
  const usage = m.usage
    ? `Includes ${m.usage.includedPerOffice.toLocaleString('en-US')} ${m.usage.unit}s per office each month; above that ${(m.usage.overageMilliCents / 10_000).toFixed(m.usage.overageMilliCents % 100 === 0 ? 2 : 3)} USD per ${m.usage.unit}.`
    : null;
  const points = [
    `${(m.priceCents / 100).toFixed(2)} USD per office per month, billed on your monthly Velora invoice${m.pricing.setupFeeCents ? ` plus a one-time ${(m.pricing.setupFeeCents / 100).toFixed(2)} USD setup fee` : ''}.`,
    ...(usage ? [usage] : []),
    `Turn it off in the Marketplace at any time: off at the end of the period, or now with a day-based credit. Within ${REFUND_WINDOW_DAYS} days of the first paid charge, turning it off refunds that charge in full.`,
    'Annual prepay is 10 months for 12 and renews monthly unless renewed; you get a reminder 30 days before the term ends.',
    'Turning it off never deletes data: reads and exports keep working for 90 days.',
    m.baaCovered
      ? `Covered by your practice's Velora BAA${m.phiLeavesVelora ? '; a carrier or partner outside Velora also handles some data (see What it can access).' : '.'}`
      : 'Not covered by the Velora BAA; the partner signs its own.',
    ...(AI_LIMITS[m.key] ? [AI_LIMITS[m.key] as string] : []),
  ];
  return { version: MODULE_TERMS_VERSION, points };
}

/* ------------------------------------------------------------------------ */
/* Per-office state                                                          */
/* ------------------------------------------------------------------------ */

/**
 * Store status of one module at one office. Mirrors the entitlement state
 * machine (`@velora/billing-square`) plus:
 * - `available`: never on (or retired), trial possible or not;
 * - `included`: granted by Velora (internal or practice-wide grant), not billed.
 */
export const OfficeModuleStatusSchema = z.enum([
  'available',
  'included',
  'pending',
  'trialing',
  'active',
  'scheduled_cancel',
  'past_due',
  'read_only',
  'paused',
  'canceled',
]);
export type OfficeModuleStatus = z.infer<typeof OfficeModuleStatusSchema>;

export const WritebackLevelSchema = z.enum(['full', 'partial', 'none', 'not_needed']);
export type WritebackLevel = z.infer<typeof WritebackLevelSchema>;

export const OfficeModuleStateSchema = z.object({
  locationId: z.uuid(),
  status: OfficeModuleStatusSchema,
  /** Full access (trialing, active, scheduled_cancel, past_due, included). */
  hasAccess: z.boolean(),
  trialAvailable: z.boolean(),
  /** Set once the one trial for this office was used. */
  trialUsedEndsAt: IsoInstant.nullable(),
  trialEndsAt: IsoInstant.nullable(),
  convertAtTrialEnd: z.boolean(),
  since: IsoInstant.nullable(),
  cancelAt: IsoInstant.nullable(),
  endedAt: IsoInstant.nullable(),
  graceEndsAt: IsoInstant.nullable(),
  readOnlyEndsAt: IsoInstant.nullable(),
  retainUntil: IsoInstant.nullable(),
  priceCents: Cents.nullable(),
  /** Billing term of the office's plan; absent or null: monthly. */
  term: BillingTermSchema.nullable().optional(),
  /** Exclusive end date of an annual term. */
  termEndsOn: IsoDate.nullable().optional(),
  reason: z.string().nullable(),
  /** A pending request from someone at this office. */
  request: z.object({ id: z.uuid(), requestedBy: z.string(), at: IsoInstant }).nullable(),
  /**
   * PMS write-back of this module at this office, from the connector's
   * capability list: every command available (`full`), some (`partial`),
   * none (`none`: changes queue in Velora for staff), or the module never
   * writes to the PMS (`not_needed`). Null when the office has no PMS.
   */
  writeback: WritebackLevelSchema.nullable(),
});
export type OfficeModuleState = z.infer<typeof OfficeModuleStateSchema>;

export const StoreOfficeSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string(),
  colorIndex: z.number().int().min(0).max(11),
  pmsSystem: PmsSystemSchema.nullable(),
  adapter: z.enum(['mock', 'pulsesync-v1', 'pulsesync-v2']).nullable(),
});
export type StoreOffice = z.infer<typeof StoreOfficeSchema>;

export const RecommendationSchema = z.object({
  moduleKey: MarketplaceModuleKeySchema,
  locationId: z.uuid().nullable(),
  /** "38 missed calls last week at Saginaw". */
  reason: z.string(),
  metric: z.number(),
});
export type Recommendation = z.infer<typeof RecommendationSchema>;

export const StoreModuleSchema = z.object({
  key: MarketplaceModuleKeySchema,
  offices: z.array(OfficeModuleStateSchema),
});
export type StoreModule = z.infer<typeof StoreModuleSchema>;

/**
 * The Square rail behind Marketplace billing. `not_configured`: this server has
 * no Square setup (ADM-11); the catalog, entitlements and requests are shown
 * read-only and buying, trials and invoices are unavailable.
 */
export const SquareModeSchema = z.enum(['simulator', 'sandbox', 'production', 'not_configured']);
export type SquareMode = z.infer<typeof SquareModeSchema>;

export const StoreResponseSchema = z.object({
  generatedAt: IsoInstant,
  timeZone: z.string(),
  offices: z.array(StoreOfficeSchema),
  /** True when the caller may buy for at least one office in scope. */
  canPurchase: z.boolean(),
  /** Offices in scope where the caller holds `marketplace.purchase` (office roles differ). */
  purchaseOfficeIds: z.array(z.uuid()),
  modules: z.array(StoreModuleSchema),
  recommendations: z.array(RecommendationSchema),
  pendingRequests: z.number().int().nonnegative(),
  squareMode: SquareModeSchema,
  /**
   * True only when this server really bills add-ons (a Square sandbox or
   * production rail AND `VELORA_MARKETPLACE_BILLING=1`). Anything else is
   * the beta no-billing hold: modules are comped, and the web never shows a
   * next-invoice amount or a price as owed. Absent counts as false.
   */
  billingLive: z.boolean().optional(),
});
export type StoreResponse = z.infer<typeof StoreResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Detail                                                                    */
/* ------------------------------------------------------------------------ */

export const WritebackAvailabilitySchema = z.object({
  command: WritebackCommandSchema,
  label: z.string(),
  available: z.boolean(),
  /** Why it is not available ("Blocked on Open Dental: ..."), or semantics when it is. */
  note: z.string().nullable(),
});

export const OfficeConnectionSchema = z.object({
  locationId: z.uuid(),
  pmsSystem: PmsSystemSchema.nullable(),
  adapter: z.enum(['mock', 'pulsesync-v1', 'pulsesync-v2']).nullable(),
  /** "PulseSync v1 (gated write list)". */
  method: z.string(),
  writeback: z.array(WritebackAvailabilitySchema),
});
export type OfficeConnection = z.infer<typeof OfficeConnectionSchema>;

export const TurnOnPreviewSchema = z.object({
  proratedCents: Cents,
  days: z.number().int(),
  periodDays: z.number().int(),
  from: IsoDate,
  periodEndExclusive: IsoDate,
  recurringCents: Cents,
  chargeImmediately: z.boolean(),
  summary: z.string(),
});
export type TurnOnPreview = z.infer<typeof TurnOnPreviewSchema>;

export const TrialPreviewSchema = z.object({
  startsOn: IsoDate,
  endsOn: IsoDate,
  reminders: z.array(IsoDate),
  hasCard: z.boolean(),
  cardLast4: z.string().nullable(),
});
export type TrialPreview = z.infer<typeof TrialPreviewSchema>;

export const ModuleDetailResponseSchema = z.object({
  key: MarketplaceModuleKeySchema,
  offices: z.array(OfficeModuleStateSchema),
  connections: z.array(OfficeConnectionSchema),
  turnOn: TurnOnPreviewSchema.nullable(),
  trial: TrialPreviewSchema.nullable(),
  canPurchase: z.boolean(),
  /** Usage this period per office, when the module is metered. */
  usage: z.array(z.object({ locationId: z.uuid(), used: z.number(), included: z.number().int() })),
});
export type ModuleDetailResponse = z.infer<typeof ModuleDetailResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Actions                                                                   */
/* ------------------------------------------------------------------------ */

export const StartTrialBodySchema = z.object({
  locationIds: z.array(z.uuid()).min(1).max(50),
  /** Convert to paid at trial end (needs a card on file). */
  consentToConvert: z.boolean().default(false),
  /** Required when the module writes to the PMS or PHI leaves Velora. */
  acknowledgedDataAccess: z.boolean().default(false),
  /** The module terms version the buyer accepted (`MODULE_TERMS_VERSION`). */
  acceptedTermsVersion: z.string().max(40).optional(),
});
export type StartTrialBody = z.input<typeof StartTrialBodySchema>;

export const TurnOnBodySchema = z.object({
  locationIds: z.array(z.uuid()).min(1).max(50),
  acknowledgedDataAccess: z.boolean().default(false),
  /** `annual`: 12 months prepaid for 10; on a monthly module, switches it to annual. */
  term: BillingTermSchema.default('monthly'),
  /** The module terms version the buyer accepted (`MODULE_TERMS_VERSION`). */
  acceptedTermsVersion: z.string().max(40).optional(),
});
export type TurnOnBody = z.input<typeof TurnOnBodySchema>;

export const TurnOffBodySchema = z.object({
  locationIds: z.array(z.uuid()).min(1).max(50),
  mode: z.enum(['period_end', 'now']),
  reason: z.string().max(200).optional(),
});
export type TurnOffBody = z.input<typeof TurnOffBodySchema>;

export const UndoTurnOffBodySchema = z.object({ locationIds: z.array(z.uuid()).min(1).max(50) });
export type UndoTurnOffBody = z.input<typeof UndoTurnOffBodySchema>;

export const ActionErrorCodeSchema = z.enum([
  'trial_unavailable',
  'already_entitled',
  'outstanding_balance',
  'no_entitlement',
  'included',
  'acknowledgement_required',
  'terms_required',
  'billing_unavailable',
  'conflict',
  'failed',
]);

export const ActionResultSchema = z.object({
  moduleKey: MarketplaceModuleKeySchema,
  results: z.array(
    z.object({
      locationId: z.uuid(),
      ok: z.boolean(),
      status: OfficeModuleStatusSchema.nullable(),
      /** Copy for the toast, for example "Off at end of period (2026-11-01)". */
      summary: z.string().nullable(),
      error: z.object({ code: ActionErrorCodeSchema, message: z.string() }).nullable(),
    }),
  ),
});
export type ActionResult = z.infer<typeof ActionResultSchema>;

export const TurnOffPreviewSchema = z.object({
  locationId: z.uuid(),
  periodEnd: z.object({ accessEndsExclusive: IsoDate, summary: z.string() }),
  now: z.object({ creditCents: Cents, unusedDays: z.number().int(), summary: z.string() }),
  retainDays: z.number().int(),
});
export type TurnOffPreview = z.infer<typeof TurnOffPreviewSchema>;

/* ------------------------------------------------------------------------ */
/* Requests (non-admins)                                                     */
/* ------------------------------------------------------------------------ */

export const CreateModuleRequestBodySchema = z.object({
  moduleKey: MarketplaceModuleKeySchema,
  locationId: z.uuid(),
  reason: z.string().trim().max(500).optional(),
});
export type CreateModuleRequestBody = z.input<typeof CreateModuleRequestBodySchema>;

export const ModuleRequestSchema = z.object({
  id: z.uuid(),
  moduleKey: MarketplaceModuleKeySchema,
  locationId: z.uuid(),
  requestedBy: z.object({ id: z.uuid(), name: z.string() }),
  reason: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'declined', 'withdrawn']),
  createdAt: IsoInstant,
  decidedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  decidedAt: IsoInstant.nullable(),
  decisionNote: z.string().nullable(),
});
export type ModuleRequest = z.infer<typeof ModuleRequestSchema>;

export const ModuleRequestListSchema = z.object({ requests: z.array(ModuleRequestSchema) });
export type ModuleRequestList = z.infer<typeof ModuleRequestListSchema>;

export const DecideModuleRequestBodySchema = z.object({
  decision: z.enum(['start_trial', 'decline']),
  note: z.string().trim().max(500).optional(),
  /** Required to start the trial: the module terms version the admin accepted. */
  acceptedTermsVersion: z.string().max(40).optional(),
});
export type DecideModuleRequestBody = z.input<typeof DecideModuleRequestBodySchema>;

/* ------------------------------------------------------------------------ */
/* Billing and modules                                                       */
/* ------------------------------------------------------------------------ */

export const InvoiceLineViewSchema = z.object({
  kind: z.enum(['module', 'proration', 'usage', 'adjustment', 'credit']),
  description: z.string(),
  moduleKey: z.string().nullable(),
  locationId: z.uuid().nullable(),
  amountCents: Cents,
});
export type InvoiceLineView = z.infer<typeof InvoiceLineViewSchema>;

export const InvoiceViewSchema = z.object({
  reference: z.string(),
  kind: z.enum(['monthly', 'immediate']),
  periodStart: IsoDate,
  periodEndExclusive: IsoDate,
  dueDate: IsoDate,
  totalCents: Cents,
  status: z.enum(['draft', 'open', 'paid', 'past_due', 'void', 'skipped']),
  lines: z.array(InvoiceLineViewSchema),
  /**
   * True when the invoice also bills offices outside the caller's view: their
   * lines are left out and `totalCents` is the sum of the lines shown.
   */
  partial: z.boolean(),
});
export type InvoiceView = z.infer<typeof InvoiceViewSchema>;

export const UsageMeterViewSchema = z.object({
  moduleKey: MarketplaceModuleKeySchema,
  meterKey: z.string(),
  label: z.string(),
  locationId: z.uuid(),
  used: z.number(),
  included: z.number().int(),
  /** Run-rate projection to period end. */
  projected: z.number(),
  overageMilliCents: z.number().int(),
  /** 0..n; 0.8 and above is a warning. */
  ratio: z.number(),
});
export type UsageMeterView = z.infer<typeof UsageMeterViewSchema>;

export const BillingOverviewSchema = z.object({
  squareMode: SquareModeSchema,
  /**
   * True only when this server really bills add-ons (a Square sandbox or
   * production rail AND `VELORA_MARKETPLACE_BILLING=1`). Anything else is
   * the beta no-billing hold: modules are comped, and the web never shows a
   * next-invoice amount or a price as owed. Absent counts as false.
   */
  billingLive: z.boolean().optional(),
  account: z.object({
    companyName: z.string(),
    billingEmail: z.string().nullable(),
    cardBrand: z.string().nullable(),
    cardLast4: z.string().nullable(),
    anchorDay: z.number().int().min(1).max(31),
    timeZone: z.string(),
  }),
  period: z.object({ start: IsoDate, endExclusive: IsoDate, daysLeft: z.number().int() }),
  nextInvoice: z.object({
    date: IsoDate,
    estimateCents: Cents,
    lines: z.array(InvoiceLineViewSchema),
  }),
  invoices: z.array(InvoiceViewSchema),
  usage: z.array(UsageMeterViewSchema),
  /** Admin dunning banner, when any module is past due. */
  dunning: z
    .object({
      since: IsoInstant,
      graceEndsAt: IsoInstant,
      modules: z.array(z.object({ moduleKey: MarketplaceModuleKeySchema, locationId: z.uuid() })),
    })
    .nullable(),
  webPayments: z
    .object({ applicationId: z.string(), locationId: z.string(), scriptUrl: z.string() })
    .nullable(),
});
export type BillingOverview = z.infer<typeof BillingOverviewSchema>;

export const SaveCardBodySchema = z.object({
  sourceToken: z.string().min(6).max(1024),
  verificationToken: z.string().max(1024).optional(),
  cardholderName: z.string().trim().min(1).max(120).optional(),
});
export type SaveCardBody = z.input<typeof SaveCardBodySchema>;

export const SaveCardResultSchema = z.object({
  cardBrand: z.string().nullable(),
  cardLast4: z.string().nullable(),
});

/** Staff notice for the shell banner (marketplace.md 4.9). */
export const BillingNoticeSchema = z.object({
  notice: z
    .object({
      audience: z.enum(['admin', 'staff']),
      severity: z.enum(['warning', 'neutral']),
      graceEndsAt: IsoInstant,
    })
    .nullable(),
});
export type BillingNotice = z.infer<typeof BillingNoticeSchema>;

/* ------------------------------------------------------------------------ */
/* 402                                                                       */
/* ------------------------------------------------------------------------ */

/** Body every module route returns without the entitlement (BUILD-PLAN M23). */
export const PaymentRequiredBodySchema = z.object({
  feature: z.string(),
  upsell_url: z.string(),
  trial_available: z.boolean(),
  reason: z.enum([
    'not_entitled',
    'trial_used',
    'trial_ended',
    'past_due',
    'read_only',
    'paused',
    'canceled',
  ]),
});
export type PaymentRequiredBody = z.infer<typeof PaymentRequiredBodySchema>;

export const FeatureAccessQuerySchema = z.object({
  feature: z.string(),
  locationId: z.uuid(),
  mode: z.enum(['read', 'write']).default('read'),
});

export const FeatureAccessResponseSchema = z.union([
  z.object({
    allowed: z.literal(true),
    access: z.enum(['full', 'read_only']),
    status: z.string(),
  }),
  z.object({ allowed: z.literal(false), body: PaymentRequiredBodySchema }),
]);
export type FeatureAccessResponse = z.infer<typeof FeatureAccessResponseSchema>;

/* ------------------------------------------------------------------------ */
/* Practice settings                                                         */
/* ------------------------------------------------------------------------ */

/** Practice Marketplace settings (Settings > Marketplace & Billing). */
export const MarketplaceSettingsSchema = z.object({
  /** "Hide modules we don't use": locked modules leave the nav and menus. */
  hideLockedNav: z.boolean(),
});
export type MarketplaceSettings = z.infer<typeof MarketplaceSettingsSchema>;

export const MarketplaceSettingsPatchSchema = MarketplaceSettingsSchema.partial();
export type MarketplaceSettingsPatch = z.infer<typeof MarketplaceSettingsPatchSchema>;

/* ------------------------------------------------------------------------ */
/* Live                                                                      */
/* ------------------------------------------------------------------------ */

/**
 * SSE entity keys published on every entitlement change. The `shell.` key
 * makes every open tab refetch its bootstrap (and so its locked panels)
 * without a reload; the `marketplace.` key refetches Marketplace screens.
 */
export const MARKETPLACE_LIVE_KEYS = ['marketplace.entitlement', 'shell.entitlement'] as const;
